import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { fieldsTypes } from 'constants/contentGrid';

import { EditorContext } from "components/Editor";
import { useChildrenWidgetSelected, useWidgetSelected } from 'components/Editor/hooks/useEditorSession';

import styles from './card.module.scss';
import { Container } from "../../core/container";
import { Property } from "components/Editor/components/properties";
import { toggleEditMode, updateWidgetProperty } from "actions/editor";
import { Background } from "components/Editor/components/properties/background";
import { BackgroundView } from "../../background";
import { CONTENT_CARD_TEMPLATE, HOTSPOT } from "constants/widgetTypes";
import Tooltip from "components/Tooltip";
import Truncate from "react-truncate";
import { toggleFieldVisibilityInContentCard } from "actions/contentGrid";
import { getBoxShadow } from "services/cssHelper.service";

const ContentCardWidget = React.memo(function ContentCardWidget({ className, style, widgetProps, widgetRef, children }) {
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const background = _.get(widgetProps, `properties.desktop.background`);

  const selected = useWidgetSelected(editorSession, id);
  const childrenSelection = useChildrenWidgetSelected(editorSession, id);

  const widgetStyles = {
    ...style,
    ...getStyles(widgetProps, breakpoint)
  }

  return (
    <Container widgetProps={widgetProps} widgetRef={widgetRef}>
      <div
        id={id}
        ref={ref}
        className={cx(className, styles.contentCard, "widget", {
          [styles.partialSelection]: childrenSelection.selected
        })}
        data-container={true}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={true}
        style={widgetStyles}
      >
        {!(selected || childrenSelection.selected) &&
          <div className={styles.overlay} onClick={(e) => {
            e.stopPropagation();
          }}></div>
        }

        <BackgroundView background={background} />
        {children}
      </div>
    </Container>
  )
});

const ContentCardDesktopAction = (props) => {
  const dispatch = useDispatch();
  const editorSession = props.editorSession;
  const breakpoint = props.breakpoint;

  const [view, setView] = useState(null);

  const id = props.uid;
  const background = _.get(props, `properties.desktop.background`);

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(false);
  }

  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='Fields' onClick={() => changeView('fields')}>
        <i className={cx("cleverstory-lines")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Background' onClick={() => changeView('background')}>
        <i className={cx("cleverstory-fill-color")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Border' onClick={() => changeView('borders')}>
        <i className={cx("cleverstory-grids")} />
      </Property.Action>
      {view === 'fields' &&
        <LayoutFieldsDialog 
          id={id}
          editorSession={editorSession}
          breakpoint={breakpoint}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          showShadow={true}
          widgetProps={props}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {view === 'borders' &&
        <BorderPropertyDialog 
          id={id}
          editorSession={editorSession}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      }
    </>
  );
}

const BorderPropertyDialog = ({ id, editorSession, widget, ...rest }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const properties = _.get(widget, `properties.${breakpoint}`, {});

  const border = _.get(properties, 'border', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      border: {
        ...border,
        color: color
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      border: {
        ...border,
        width: value
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      border: {
        ...border,
        radius: radius
      }
    }

    handlePropertyChange(request);
  }

  return (
    <>
      <Property.Dialog className={styles.fontDialog}
        width={246}
        target={`#${id}`}
        title={'Border'}
        onCancel={rest.onCancel}
      >
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
        <Property name="Stroke Width" className={styles.buttonStyleProperty}>
          <Property.Slider
            min={0}
            max={10}
            step={1}
            input
            value={borderWidth}
            onChange={handleBorderWidthChange}
          />
        </Property>
        <Property name="Corner Radius" className={styles.formStyleProperty}>
          <Property.BorderRadius
            value={borderRadius}
            onChange={handleBorderRadiusChange}
          />
        </Property>
      </Property.Dialog>
    </>
  );
};

const LayoutFieldsDialog = ({ editorSession, id, breakpoint, ...rest }) => {
  const dispatch = useDispatch();

  const template = _.get(rest, 'widget.template.widget');
  const contentGrid = _.get(rest, 'widget.template.contentGrid');

  const cardTemplate = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    return widgets.find(item => item.uid === template && item.type === CONTENT_CARD_TEMPLATE);
  });
  
  const fields = useSelector(state => {
    const items = _.get(state, `fields.items`) || [];
    return items;
  });

  const basicFields = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];

    const _findFieldByName = (name, parent) => {
      const widget = widgets.find(item => item.uid === parent);
      if (!widget) {
        return null;
      }

      if (widget.parameter === name) {
        return widget;
      }

      return widgets.filter(item => item.parent === parent).find(item => {
        return _findFieldByName(name, item.uid);
      });
    }

    return fieldsTypes.map(field => {
      const widget = _findFieldByName(field.parameter, rest.widget.uid);
      const visible = _.get(widget, `properties.${breakpoint}.visible`);

      const template = widgets.find(item => item.uid === _.get(widget, 'template.widget'));
      if(template && !_.get(template, `properties.${breakpoint}.visible`)){
        return null;
      }

      return {
        ...field,
        widget: widget,
        visible: visible
      };
    }).filter(item => item && item.widget != null);
  });

  const customWidgets = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];

    return widgets.filter(item => {
      return item.parent === rest.widget.uid && (item.parameter == null || item.parameter === "cta")
    });
  });

  const handleBasicFieldClicked = (field, visibility) => {
    dispatch(toggleFieldVisibilityInContentCard(editorSession, rest.widget, field.widget, breakpoint, visibility));
  }

  const handleCustomFieldClicked = (widget, visibility) => {
    dispatch(toggleFieldVisibilityInContentCard(editorSession, rest.widget, widget, breakpoint, visibility));
  }

  const customFields = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    const fields = _.get(state, `fields.items`) || [];

    const _findWidgetByFieldId = (field, parent) => {
      let widget = widgets.find(item => item.parameter === "custom_" + field.id && item.parent === parent);

      if(!widget && _.isUndefined(widget)) {
        return null;
      }

      let customField = fields.find(item => item.id === field.id);

      if(customField) {
        widget = {
          ...widget,
          name: customField.name
        }

        return widget;
      }

      return null;
    }

    return fields.map(field => {
      const fieldWidget = _findWidgetByFieldId(field, rest.widget.uid);

      if(fieldWidget) {
        return fieldWidget;
      }

      return null;
    });
    
  }).filter(item => item !== null);

  const handleFieldClick = (widget, visibility) => {
    dispatch(toggleFieldVisibilityInContentCard(editorSession, rest.widget, widget, breakpoint, visibility));
  }

  return (
    <Property.Dialog
      className={styles.fieldsDialog}
      width={230}
      target={`#${id}`}
      title={"Card Fields"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <div className={styles.fields}>
          <div className={styles.group}>Basic Fields</div>
          {basicFields.map(field => {
            const visible = _.get(field, `widget.properties.${breakpoint}.visible`);

            return (
              <div className={styles.field}>
                <span className={styles.name}>
                  <Truncate lines={1}>{field.name}</Truncate>
                </span>
                <Tooltip className={styles.tooltip} placement='top' content={visible ? `Hide in card` : `Show in card`}>
                  <button className={styles.action} onClick={() => handleBasicFieldClicked(field, !visible)}>
                    <i className={cx(styles.icon, { 'cleverstory-view': visible, 'cleverstory-hide': !visible })} />
                  </button>
                </Tooltip>
              </div>
            );
          })}
        </div>
        {_.size(customWidgets) > 0 &&
          <div className={styles.fields}>
            <div className={styles.group}>Custom Widgets</div>
            {customWidgets.map(widget => {              
              const visible = _.get(widget, `properties.${breakpoint}.visible`);

              return (
                <div className={styles.field}>
                  <span className={styles.name}>
                    <Truncate lines={1}>{widget.name}</Truncate>
                  </span>
                  <Tooltip className={styles.tooltip} placement='top' content={visible ? `Hide in card` : `Show in card`}>
                    <button className={styles.action} onClick={() => handleCustomFieldClicked(widget, !visible)}>
                      <i className={cx(styles.icon, { 'cleverstory-view': visible, 'cleverstory-hide': !visible })} />
                    </button>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        }
        {_.size(customFields) > 0 &&
          <div className={styles.fields}>
            <div className={styles.group}>Custom Fields</div>
            {customFields.map(customField => {
              const visible = _.get(customField, `properties.${breakpoint}.visible`);

              return (
                <div className={styles.field}>
                  <span className={styles.name}>
                    <Truncate lines={1}>{customField.name}</Truncate>
                  </span>
                  <Tooltip className={styles.tooltip} placement='top' content={visible ? `Hide in card` : `Show in card`}>
                    <button className={styles.action} onClick={() => handleFieldClick(customField, !visible)}>
                      <i className={cx(styles.icon, { 'cleverstory-view': visible, 'cleverstory-hide': !visible })} />
                    </button>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        }
      </Property.Body>
    </Property.Dialog>
  );
}


ContentCardWidget.actions = (editorSession, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <ContentCardDesktopAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
      {breakpoint === 'mobile' &&
        <ContentCardDesktopAction
          editorSession={editorSession}
          breakpoint={breakpoint}
          {...props}
        />
      }
    </>
  );
}

ContentCardWidget.moveableConfig = (widget) => {
  return {
    draggable: false,
    resizable: true,
    rotatable: false,
    deletable: false,
    cloneable: false,
    renderDirections: ["n", "s", "ne", "nw", "se", "sw", "e", "w"],
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set('transform', 'translate', '0px, 0px, 0px');
      return frame.get();
    },
    onReparent: (widget, parent) => {
      var parentElement = parent ? document.getElementById(parent) : null;
      if (parentElement && parentElement.getAttribute('data-widget-type') === HOTSPOT) {
        return false;
      }

      return false;
    }
  };
};

ContentCardWidget.processRequest = (widget) => {
  return null;
}

/**
 * Generate Styles
 * 
*/
function getStyles(widget, breakpoint) {
  if (!widget) {
    return null;
  }

  const border = _.get(widget, `properties.${breakpoint}.border`);

  var styles = {};
  const topLeft = _.get(border, 'radius.topLeft', null);
  const topRight = _.get(border, 'radius.topRight', null);
  const bottomLeft = _.get(border, 'radius.bottomLeft', null);
  const bottomRight = _.get(border, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(border, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(border, 'width', 0);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;

  //DropShadow
  const shadow = _.get(widget, `properties.${breakpoint}.shadow`, {});
  const shadowStyle = getBoxShadow(shadow);
  styles = {
    ...styles,
    ...shadowStyle
  }

  return styles;
}

export { ContentCardWidget };